import { getLocaleString } from 'utilities/locales';
import { trackCdp } from 'utilities/tracking';
import ExceptionReporter from 'application/exception_reporter';

window.Sq = window.Sq || {};
Sq.Shop = Sq.Shop || {};
$(() => Sq.Shop.WebPaymentSdk.init());

Sq.Shop.WebPaymentSdk = {
  utils: Sq.Shop.Utils,

  async init() {
    if ($('.web-payment-sdk').length === 0) {
      return;
    }

    this.getHtml();
    this.getData();
    await this.createForm();
    this.bindEvents();
  },

  getHtml() {
    this.$form = $('.web-payment-sdk');
    this.$digitalWalletInfo = $('#digital-wallet-info');
    this.$scaInfo = $('#sca-info');
    this.$submitButton = $('#card-button');
    this.$googlePayButton = $('#google-pay-button');
  },

  getData() {
    this.dataPostUrl = this.$form.attr('data-post-url');
    this.dataCompleteUrl = this.$form.attr('data-complete-url');
    this.orderNumber = $('#order-number').val();
    this.payapiAppId = $('#payapi-app-id').val();
    this.locationId = $('#payapi-location-id').val();
    this.authToken = $('meta[name=csrf-token]').attr('content');
    this.cardIntent = $('#cardIntent').val();
  },

  // shop logic (handle create payment / complete order)
  handleSuccess(nonce, cardData, verificationToken) {
    $.ajax({
      url: this.dataPostUrl,
      type: 'POST',
      data: {
        nonce,
        card_data: {
          card_brand: cardData.brand,
          last_4: cardData.last4,
          exp_year: cardData.expYear,
          exp_month: cardData.expMonth,
        },
        order_number: this.orderNumber,
        authenticity_token: this.authToken,
        verification_token: verificationToken,
      },
    })
      .done(() => {
        window.location = this.dataCompleteUrl;
      })
      .fail((xhr) => {
        this.enableSubmits();
        this.utils.flashBanner().update({
          message: `${xhr.status} error submitting payment. Please try again.`,
          state: 'error',
          timeout: false,
        });
      });
  },

  // web payment sdk stuff

  buildPaymentRequest(payments) {
    return payments.paymentRequest({
      countryCode: this.getDigitalWalletField('country').val(),
      currencyCode: this.getDigitalWalletField('currency').val(),
      total: {
        amount: this.getDigitalWalletField('first_payment_total').val(),
        label: 'Square Shop',
      },
    });
  },

  verificationDetails() {
    const cardIntent = this.cardIntent.toUpperCase();
    return {
      intent: cardIntent,
      squareProduct: 'MARKET',
      billingContact: {
        givenName: this.getScaField('given_name').val(),
        familyName: this.getScaField('family_name').val(),
      },
      ...(['CHARGE', 'CHARGE_AND_STORE'].includes(cardIntent)
        ? {
            amount: this.getScaField('amount').val(),
            currencyCode: Sq.Environment['currency_code'],
          }
        : {}),
    };
  },

  // Emits the same verifyBuyer CDP events as utilities/shop_payments.{js,ts}.
  async runSCA(token) {
    const cardIntent = this.cardIntent && this.cardIntent.toUpperCase();
    let verificationResults;
    try {
      verificationResults = await this.payments.verifyBuyer(
        token,
        this.verificationDetails(),
      );
    } catch (e) {
      trackCdp({
        name: 'verifyBuyer error',
        params: {
          feature: 'shop',
          surface: 'change_card_on_file',
          order_number: this.orderNumber,
          card_intent: cardIntent,
          payment_method: 'card',
          error_message: e && e.message,
          error_name: e && e.name,
          error_type: e && e.errors && e.errors[0] && e.errors[0].type,
          error_code: e && e.errors && e.errors[0] && e.errors[0].code,
        },
      });
      // tokenize() skips the Sentry capture for rejections already tracked here.
      if (e) {
        e.verifyBuyerTracked = true;
      }
      throw e;
    }

    const responseFields = verificationResults || {};
    trackCdp({
      name: 'verifyBuyer response',
      params: {
        feature: 'shop',
        surface: 'change_card_on_file',
        order_number: this.orderNumber,
        user_challenged: Boolean(responseFields.userChallenged),
        card_intent: cardIntent,
        // This surface only takes card entry — constant, but kept so the
        // event property set matches the checkout surfaces.
        payment_method: 'card',
        has_verification_token: Boolean(responseFields.token),
      },
    });

    if (!verificationResults) {
      // verifyBuyer's return type includes null; tracked above, fail here.
      throw new TypeError('verifyBuyer resolved without a response');
    }
    return verificationResults.token;
  },

  async tokenize(paymentMethod) {
    this.disableSubmits();
    let token;
    let cardData;
    let scaToken;
    try {
      const tokenResult = await paymentMethod.tokenize();
      if (tokenResult.status !== 'OK') {
        this.enableSubmits();
        return;
      }
      token = tokenResult.token;
      scaToken = await this.runSCA(token);
      cardData = tokenResult.details.card;
    } catch (e) {
      // Re-enable submits and surface an error so the buyer can retry. verifyBuyer
      // rejections are expected outcomes already tracked by runSCA; report anything else.
      if (!(e && e.verifyBuyerTracked)) {
        ExceptionReporter.capture(e);
      }
      this.enableSubmits();
      this.utils.flashBanner().update({
        message: 'Error processing payment. Please try again.',
        state: 'error',
        timeout: false,
      });
      return;
    }
    // Keep outside the try: the catch re-enables submits, which would open a
    // double-submit window while handleSuccess's POST is in flight.
    this.handleSuccess(token, cardData, scaToken);
  },

  async initializeCard(payments) {
    const card = await payments.card();
    await card.attach('#card-container');
    return card;
  },

  async initializeGooglePay(payments) {
    const paymentRequest = this.buildPaymentRequest(payments);

    const googlePay = await payments.googlePay(paymentRequest);
    await googlePay.attach('#google-pay-button');

    return googlePay;
  },

  async initializeApplePay(payments) {
    const paymentRequest = this.buildPaymentRequest(payments);
    const applePay = await payments.applePay(paymentRequest);
    // Note: You do not need to `attach` applePay.
    return applePay;
  },

  async createForm() {
    if (!window.Square) {
      throw new Error('Square.js failed to load properly');
    }
    const payments = window.Square.payments(this.payapiAppId, this.locationId);
    payments.setLocale(getLocaleString());
    try {
      this.card = await this.initializeCard(payments);
    } catch {
      return;
    }
    if (this.$digitalWalletInfo) {
      try {
        this.googlePay = await this.initializeGooglePay(payments);
      } catch {
        // do nothing
      }
      try {
        this.applePay = await this.initializeApplePay(payments);
      } catch {
        // do nothing
      }
    }
    this.payments = payments; // this needs to be saved for the SCA call
  },

  // bind

  bindEvents() {
    this.$submitButton.on('click', () => this.tokenize(this.card));
    if (this.googlePay) {
      this.$googlePayButton.on('click', () => this.tokenize(this.googlePay));
    }
    if (this.applePay) {
      this.$applePayButton.on('click', () => this.tokenize(this.applePay));
    }
  },

  // getting fields

  getDigitalWalletField(id) {
    return this.$digitalWalletInfo.find(`#${id}`);
  },

  getScaField(id) {
    return this.$scaInfo.find(`#${id}`);
  },

  // enable/disable submit

  enableSubmits() {
    this.$submitButton.removeClass('disabled').prop('disabled', false);
    if (this.$applePayButton) {
      this.$applePayButton.removeClass('disabled').prop('disabled', false);
    }
    if (this.$googlePayButton) {
      this.$googlePayButton.removeClass('disabled').prop('disabled', false);
    }
  },

  disableSubmits() {
    this.$submitButton.addClass('disabled').prop('disabled', true);
    if (this.$applePayButton) {
      this.$applePayButton.addClass('disabled').prop('disabled', true);
    }
    if (this.$googlePayButton) {
      this.$googlePayButton.addClass('disabled').prop('disabled', true);
    }
  },
};
